import { ListItem, ListItemText, Typography } from "@mui/material";
import { VoiceCatalogBorder, ListItemBackground } from "shared/constants";
import { titleCase } from "shared/utils/tts-custom-editor";
import { getVoiceDescription } from "shared/utils/tts";
import { voicesDescriptionAtom } from "atoms";
import { useAtomValue } from "jotai";
import { useState } from "react";
import VoiceAvatarIcon from "./VoiceAvatarIcon";
import LanguageChipWithColor from "./LanguageChipWithColor";
import VoicePlayButton from "./VoicePlayButton";
import { VoiceDescriptionProps } from "shared/models";

export interface VoiceItemProps {
  voice: string;
  handleVoiceSelection: (voiceName: string) => void;
  isSelected: boolean;
  handlePlayClick: (voiceName: string) => void;
  showStopButton: boolean;
  showInProgress: boolean;
  showSecondaryText?: boolean;
}

export default function VoiceItem({
  voice,
  handleVoiceSelection,
  isSelected,
  handlePlayClick,
  showStopButton,
  showInProgress,
  showSecondaryText = true,
}: VoiceItemProps): JSX.Element {
  const voicesDescription = useAtomValue(voicesDescriptionAtom);

  const voiceDescription: VoiceDescriptionProps | undefined =
    getVoiceDescription(voice, voicesDescription);

  const [isHovered, setIsHovered] = useState(false);

  const shouldShowPlayButton =
    isSelected || showStopButton || showInProgress || isHovered;

  return (
    <ListItem
      key={voiceDescription?.voiceName || voice}
      id={voiceDescription?.voiceName || voice}
      sx={{
        cursor: "pointer",
        border: isSelected
          ? `1px solid ${VoiceCatalogBorder.Selected}`
          : `1px solid transparent`,
        "&:hover": {
          backgroundColor: isSelected ? "" : "rgba(0, 0, 0, 0.04)",
          border: "",
        },
        backgroundColor: isSelected ? ListItemBackground.Selected : "white",
      }}
      onClick={() => handleVoiceSelection(voiceDescription?.voiceName || voice)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      secondaryAction={
        shouldShowPlayButton && (
          <VoicePlayButton
            showInProgress={showInProgress}
            showStopButton={showStopButton}
            onPlayClick={() =>
              handlePlayClick(voiceDescription?.voiceName || voice)
            }
          />
        )
      }
    >
      {voiceDescription && (
        <VoiceAvatarIcon voiceDescription={voiceDescription} />
      )}
      <ListItemText
        primary={
          <Typography variant="subtitle2" color="text.primary">
            {voiceDescription ? titleCase(voiceDescription.voiceName) : voice}
          </Typography>
        }
        secondaryTypographyProps={{ component: "div" }}
        secondary={
          showSecondaryText &&
          voiceDescription && (
            <LanguageChipWithColor
              status={voiceDescription.nativeLanguage.toLowerCase()}
            />
          )
        }
      />
    </ListItem>
  );
}
